import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const Settings = () => {
  const { user, session } = useAuth();
  const userProfile = user;
  const [settings, setSettings] = useState({
    // Privacy & Data Control
    allowAILearning: true,
    shareAnonymousAnalytics: true,
    crossPlatformLearning: false,
    profileVisibility: false, // Premium feature
    
    // Notification Settings
    emailNotifications: true,
    pushNotifications: true,
    marketingEmails: false,
    
    // AI Enhancement Settings
    autoInject: true,
    injectionFrequency: 8,
    systemInjectionMode: 'auto', // 'auto', 'system-only', 'hybrid', 'message-only'
    stealthMode: true,
    
    // Advanced Settings
    debugMode: false,
    betaFeatures: false
  });

  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      // Load settings from storage or API
      let stored = null;
      
      // Load from localStorage for web version
const savedSettings = localStorage.getItem('personalityai_settings');
if (savedSettings) {
  stored = JSON.parse(savedSettings);
}
      
      if (stored) {
        setSettings(prev => ({ ...prev, ...stored }));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    setSaveStatus('Saving...');
    
    try {
     // Save to localStorage for web version
localStorage.setItem('personalityai_settings', JSON.stringify(settings));
      
      // Save to backend using your API_BASE
      if (session?.access_token) {
        const API_BASE = 'https://personality-backend-production.up.railway.app';
        
        const response = await fetch(`${API_BASE}/api/user/settings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            user_id: user.id,
            settings: settings
          })
        });
        
        const data = await response.json();
        if (!data.success) {
          console.warn('Backend settings save failed:', data.error);
        }
      }
      
      setSaveStatus('✅ Settings saved successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveStatus('❌ Failed to save settings');
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (setting) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleSliderChange = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: parseInt(value)
    }));
  };

  const handleModeChange = (mode) => {
    setSettings(prev => ({
      ...prev,
      systemInjectionMode: mode
    }));
  };

  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'personalityai-settings.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const importSettings = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          setSettings(imported);
          setSaveStatus('✅ Settings imported successfully!');
          setTimeout(() => setSaveStatus(''), 3000);
        } catch (error) {
          setSaveStatus('❌ Invalid settings file');
          setTimeout(() => setSaveStatus(''), 3000);
        }
      };
      reader.readAsText(file);
    }
  };

  const deleteAllData = async () => {
    if (window.confirm('Are you sure you want to delete ALL your PersonalityAI data? This cannot be undone.')) {
      try {
        const API_BASE = 'https://personality-backend-production.up.railway.app';
        const response = await fetch(`${API_BASE}/api/user/data/delete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({ user_id: user.id })
        });
        const data = await response.json();
        if (data.success) {
          alert('All data deleted successfully!');
          // Optionally, clear local storage or reset settings
          localStorage.removeItem('personalityai_settings');
          setSettings({
            allowAILearning: true,
            shareAnonymousAnalytics: true,
            crossPlatformLearning: false,
            profileVisibility: false,
            emailNotifications: true,
            pushNotifications: true,
            marketingEmails: false,
            autoInject: true,
            injectionFrequency: 8,
            systemInjectionMode: 'auto',
            stealthMode: true,
            debugMode: false,
            betaFeatures: false
          });
          setSaveStatus('✅ All data deleted');
          setTimeout(() => setSaveStatus(''), 3000);
        } else {
          alert('Failed to delete data: ' + data.error);
          setSaveStatus('❌ Failed to delete data');
          setTimeout(() => setSaveStatus(''), 3000);
        }
      } catch (error) {
        console.error('Failed to delete data:', error);
        alert('Failed to delete data due to network error.');
        setSaveStatus('❌ Failed to delete data');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">⚙️ Settings</h1>
        <p className="text-gray-600">
          Manage your PersonalityAI preferences, privacy settings, and AI enhancement controls.
        </p>
        
        {saveStatus && (
          <div className={`mt-4 p-3 rounded-lg ${
            saveStatus.includes('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {saveStatus}
          </div>
        )}
      </div>

      {/* Privacy & Data Control */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">🔒 Privacy & Data Control</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <h3 className="font-medium text-gray-900">Allow AI Learning</h3>
              <p className="text-sm text-gray-600">Let PersonalityAI learn from your conversations to improve your profile</p>
            </div>
            <button
              onClick={() => handleToggle('allowAILearning')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.allowAILearning ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.allowAILearning ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <h3 className="font-medium text-gray-900">Share Anonymous Analytics</h3>
              <p className="text-sm text-gray-600">Help improve PersonalityAI by sharing anonymous usage data</p>
            </div>
            <button
              onClick={() => handleToggle('shareAnonymousAnalytics')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.shareAnonymousAnalytics ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.shareAnonymousAnalytics ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <h3 className="font-medium text-gray-900">Cross-Platform Learning</h3>
              <p className="text-sm text-gray-600">Allow learning from all connected AI platforms</p>
            </div>
            <button
              onClick={() => handleToggle('crossPlatformLearning')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.crossPlatformLearning ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.crossPlatformLearning ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div className="flex items-center space-x-2">
              <h3 className="font-medium text-gray-900">Profile Visibility</h3>
              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Premium</span>
            </div>
            <p className="text-sm text-gray-600">Make your personality insights visible to team members (Enterprise only)</p>
            <button
              disabled
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 opacity-50 cursor-not-allowed"
            >
              <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
            </button>
          </div>
        </div>
      </div>

      {/* AI Enhancement Settings */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">🧠 AI Enhancement Settings</h2>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <h3 className="font-medium text-gray-900">Auto-Inject Personality</h3>
              <p className="text-sm text-gray-600">Automatically enhance your prompts with personality context</p>
            </div>
            <button
              onClick={() => handleToggle('autoInject')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.autoInject ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.autoInject ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          <div className="py-3 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-gray-900">Injection Frequency</h3>
                <p className="text-sm text-gray-600">How often to inject personality (every N messages)</p>
              </div>
              <span className="text-sm font-medium text-purple-600">{settings.injectionFrequency}</span>
            </div>
            <input
              type="range"
              min="1"
              max="20"
              value={settings.injectionFrequency}
              onChange={(e) => handleSliderChange('injectionFrequency', e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Frequent (1)</span>
              <span>Balanced (8)</span>
              <span>Sparse (20)</span>
            </div>
          </div>

          <div className="py-3 border-b border-gray-100">
            <h3 className="font-medium text-gray-900 mb-3">System Injection Mode</h3>
            <p className="text-sm text-gray-600 mb-4">Choose how personality context is injected</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {['auto', 'system-only', 'hybrid', 'message-only'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => handleModeChange(mode)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    settings.systemInjectionMode === mode
                      ? 'bg-purple-100 border-purple-300 text-purple-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1).replace('-', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <h3 className="font-medium text-gray-900">Stealth Mode</h3>
              <p className="text-sm text-gray-600">Hide injection notifications and keep enhancements invisible</p>
            </div>
            <button
              onClick={() => handleToggle('stealthMode')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.stealthMode ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.stealthMode ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">🔔 Notification Settings</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <h3 className="font-medium text-gray-900">Email Notifications</h3>
              <p className="text-sm text-gray-600">Receive updates about your personality insights and usage</p>
            </div>
            <button
              onClick={() => handleToggle('emailNotifications')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.emailNotifications ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <h3 className="font-medium text-gray-900">Push Notifications</h3>
              <p className="text-sm text-gray-600">Get notified about important updates and features</p>
            </div>
            <button
              onClick={() => handleToggle('pushNotifications')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.pushNotifications ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.pushNotifications ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <h3 className="font-medium text-gray-900">Marketing Emails</h3>
              <p className="text-sm text-gray-600">Receive tips, tutorials, and product updates</p>
            </div>
            <button
              onClick={() => handleToggle('marketingEmails')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.marketingEmails ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.marketingEmails ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">🔧 Advanced Settings</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div>
              <h3 className="font-medium text-gray-900">Debug Mode</h3>
              <p className="text-sm text-gray-600">Enable detailed logging and debugging information</p>
            </div>
            <button
              onClick={() => handleToggle('debugMode')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.debugMode ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.debugMode ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div>
              <h3 className="font-medium text-gray-900">Beta Features</h3>
              <p className="text-sm text-gray-600">Access experimental features and early releases</p>
            </div>
            <button
              onClick={() => handleToggle('betaFeatures')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.betaFeatures ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.betaFeatures ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">💾 Data Management</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">📤 Export Settings</h3>
            <p className="text-sm text-gray-600 mb-3">Download your complete settings configuration</p>
            <button
              onClick={exportSettings}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Export Settings
            </button>
          </div>

          <div className="p-4 border rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">📥 Import Settings</h3>
            <p className="text-sm text-gray-600 mb-3">Upload a previously exported settings file</p>
            <input
              type="file"
              accept=".json"
              onChange={importSettings}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
            />
          </div>

          <div className="p-4 border border-orange-200 rounded-lg bg-orange-50">
            <h3 className="font-medium text-orange-900 mb-2">🔄 Reset Settings</h3>
            <p className="text-sm text-orange-700 mb-3">Clear all settings data and start fresh</p>
            <button
              onClick={() => {
              if (window.confirm('Are you sure you want to reset all settings? This cannot be undone.')) {
                  setSettings({
                    allowAILearning: true,
                    shareAnonymousAnalytics: true,
                    crossPlatformLearning: false,
                    profileVisibility: false,
                    emailNotifications: true,
                    pushNotifications: true,
                    marketingEmails: false,
                    autoInject: true,
                    injectionFrequency: 8,
                    systemInjectionMode: 'auto',
                    stealthMode: true,
                    debugMode: false,
                    betaFeatures: false
                  });
                  setSaveStatus('✅ Settings reset to defaults');
                  setTimeout(() => setSaveStatus(''), 3000);
                }
              }}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Reset to Defaults
            </button>
          </div>

          <div className="p-4 border border-red-200 rounded-lg bg-red-50">
            <h3 className="font-medium text-red-900 mb-2">🗑️ Delete All Data</h3>
            <p className="text-sm text-red-700 mb-3">Permanently delete your personality profile</p>
            <button
              onClick={deleteAllData}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Delete All Data
            </button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={loading}
          className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
        >
          {loading && (
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          <span>{loading ? 'Saving...' : 'Save All Settings'}</span>
        </button>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
        }
        
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default Settings;
